import { useState, useEffect, useRef } from "react";

export function AutoSkipCountdown({ onSkip, seconds = 3, t = (fr) => fr }) {
  const [remaining, setRemaining] = useState(seconds);
  const [paused, setPaused] = useState(false);
  const firedRef = useRef(false);
  const pausedRef = useRef(false);

  // Reset guards on mount (and if `seconds` changes) — defensive against instance reuse.
  useEffect(() => {
    firedRef.current = false;
    pausedRef.current = false;
    setPaused(false);
    setRemaining(seconds);
  }, [seconds]);

  // Pure countdown — no side effects inside the updater. React StrictMode
  // double-invokes updater functions; calling onSkip() from inside one
  // (as the previous implementation did) fired it twice per tick, advancing
  // two night roles instead of one. Decrementing here is side-effect-free.
  useEffect(() => {
    const interval = setInterval(() => {
      if (pausedRef.current || firedRef.current) return;
      setRemaining(r => (r > 0 ? r - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Side effect lives here, guarded to fire at most once per mount —
  // this is the ONLY place onSkip is ever invoked automatically.
  useEffect(() => {
    if (remaining <= 0 && !firedRef.current && !pausedRef.current) {
      firedRef.current = true;
      onSkip();
    }
  }, [remaining]);

  const handlePause = () => {
    const next = !paused;
    setPaused(next);
    pausedRef.current = next;
  };

  const handleSkip = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onSkip();
  };

  return (
    <div className="flex gap-2">
      <button onClick={handleSkip}
        className="flex-1 flex items-center justify-center gap-2 rounded-2xl py-3
          bg-indigo-950/60 border border-indigo-800/30 text-indigo-300 text-sm active:scale-95 transition-all">
        {!paused && (
          <span className="w-7 h-7 rounded-full bg-indigo-900/60 flex items-center justify-center
            text-indigo-200 font-bold text-sm">{remaining}</span>
        )}
        <span>{t("Continuer","Continue")}</span>
      </button>
      <button onClick={handlePause}
        className="w-11 h-11 rounded-2xl bg-stone-900/60 border border-stone-700/30
          text-stone-400 flex items-center justify-center text-base active:scale-95 transition-all">
        {paused ? "▶" : "⏸"}
      </button>
    </div>
  );
}

